import React, { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import avsApi from '../../../../../../api/avs';
import {
  avsCarrierDataWarning,
  avsErrorMessage,
  avsFieldResult,
  avsFormValues,
  avsFreshnessResult,
  avsHistoryRows,
  avsOutcome,
  avsProviderDetails,
  avsRequestOverrides,
  avsRunDisabledReason,
  formatAvsDate,
  isAvsDataStale
} from './avs';

const Badge = ({ result }) => (
  <span className={`verification-badge verification-badge-${result.tone}`}>
    {result.label}
  </span>
);

const AvsField = ({ label, value, onChange, wide, inputMode, maxLength }) => (
  <label
    className={`verification-field${wide ? ' verification-field-wide' : ''}`}
  >
    <span className="verification-field-label">{label}</span>
    <input
      className="verification-field-value"
      aria-label={label}
      value={value || ''}
      onChange={onChange}
      readOnly={!onChange}
      inputMode={inputMode}
      maxLength={maxLength}
      autoComplete="off"
      onKeyDown={event => {
        if (event.key === 'Enter') event.preventDefault();
      }}
    />
  </label>
);

const AvsHistoryEntry = ({ check, expanded }) => {
  const providerDetails = avsProviderDetails(check);

  return (
    <details className="verification-accordion" open={expanded}>
      <summary className="verification-accordion-summary">
        <span>
          <strong>{formatAvsDate(check.created)}</strong>
          <span className="verification-subtitle">
            {check.business_name || 'Company unavailable'}
            {' · '}
            {check.account_number_redacted
              ? `Account ending ${check.account_number_redacted}`
              : 'Account unavailable'}
          </span>
        </span>
        <Badge result={avsOutcome(check.outcome)} />
      </summary>
      <div className="avs-history-content">
        <p className="verification-subtitle">
          Routing number: {check.routing_number || 'Unavailable'}
          {' · '}Run by: {check.triggered_by_email || 'Unavailable'}
        </p>
        {check.outcome === 'ERROR' && (
          <p className="verification-error" role="status">
            {check.error_detail || 'The AVS check could not be completed.'}
          </p>
        )}
        {providerDetails.length > 0 && (
          <section
            className="avs-provider-response"
            aria-label="Wells Fargo response"
          >
            <h4 className="avs-provider-response-title">
              Wells Fargo response
            </h4>
            <dl className="avs-provider-details">
              {providerDetails.map(([label, value]) => (
                <div className="avs-provider-detail" key={label}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          </section>
        )}
        <table className="avs-history-table">
          <thead>
            <tr>
              <th scope="col">Field</th>
              <th scope="col">Sent</th>
              <th scope="col">Result</th>
            </tr>
          </thead>
          <tbody>
            {avsHistoryRows(check).map(([label, sent, result]) => (
              <tr key={label}>
                <th scope="row">{label}</th>
                <td>{sent || 'Not provided'}</td>
                <td>
                  <Badge result={avsFieldResult(result)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
};

const AvsHistory = ({ checks }) => (
  <section className="avs-history" aria-label="AVS history">
    <h3 className="verification-section-title">
      AVS history ({checks.length}){checks.length > 0 && ' · newest expanded'}
    </h3>
    {!checks.length && (
      <p className="verification-empty">
        No AVS checks have been run for this carrier.
      </p>
    )}
    {checks.map((check, index) => (
      <AvsHistoryEntry check={check} expanded={index === 0} key={check.id} />
    ))}
  </section>
);

const AvsPanelContent = ({ paymentProfile, verification }) => {
  const [prefill, setPrefill] = useState(null);
  const [edits, setEdits] = useState({});
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [disabled, setDisabled] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [now, setNow] = useState(Date.now());
  const mounted = useRef(true);
  const pending = useRef(false);
  const refreshQueued = useRef(false);
  const requestId = useRef(0);
  const profileId = paymentProfile.id;
  const carrierok = (verification && verification.carrierok) || {};
  const isDummy = carrierok.carrierok_id === 'dummy_data';
  const carrierokRefreshed = Date.parse(carrierok.created);
  const prefillRefreshed = Date.parse(
    prefill && prefill.verification_last_refreshed
  );
  const carrierDataConfirmed = !isDummy && Number.isFinite(carrierokRefreshed);
  const carrierDataMatches =
    carrierDataConfirmed && carrierokRefreshed === prefillRefreshed;
  const values = avsFormValues(prefill || {}, edits);

  useEffect(
    () => () => {
      mounted.current = false;
      requestId.current += 1;
    },
    []
  );

  const refresh = useCallback(
    async (retryDisabled = false) => {
      if (pending.current) {
        refreshQueued.current = true;
        return;
      }
      const currentRequest = ++requestId.current;
      setLoading(true);
      setError('');
      try {
        const result = await avsApi.getChecks(profileId);
        if (!mounted.current || currentRequest !== requestId.current) return;
        setPrefill(result.prefill || null);
        setChecks(result.checks || []);
        if (!result.prefill) {
          setLastRefreshed(null);
          setError('AVS input data is unavailable. Refresh to try again.');
          return;
        }
        if (retryDisabled) setDisabled(false);
        setLastRefreshed(new Date().toISOString());
        setNow(Date.now());
      } catch (requestError) {
        if (mounted.current && currentRequest === requestId.current) {
          setError(avsErrorMessage(requestError));
        }
      } finally {
        if (mounted.current && currentRequest === requestId.current)
          setLoading(false);
      }
    },
    [profileId]
  );

  useEffect(() => {
    refresh();
  }, [refresh, carrierok.created]);

  useEffect(() => {
    if (!prefill || isAvsDataStale(prefill)) return undefined;
    const expires =
      Date.parse(prefill.verification_last_refreshed) + 24 * 60 * 60 * 1000;
    const timer = setTimeout(
      () => setNow(Date.now()),
      Math.max(expires - Date.now() + 1, 0)
    );
    return () => clearTimeout(timer);
  }, [prefill]);

  const run = async () => {
    if (pending.current || loading || disabled) return;
    const reason = avsRunDisabledReason(prefill, values);
    if (reason) {
      setError(reason);
      setNow(Date.now());
      return;
    }
    pending.current = true;
    setRunning(true);
    setError('');
    try {
      const check = await avsApi.runCheck(
        profileId,
        avsRequestOverrides(values)
      );
      if (!mounted.current) return;
      setChecks(previous => [
        check,
        ...previous.filter(item => item.id !== check.id)
      ]);
    } catch (requestError) {
      if (!mounted.current) return;
      setError(avsErrorMessage(requestError));
      if (requestError.response && requestError.response.status === 503)
        setDisabled(true);
    } finally {
      pending.current = false;
      if (mounted.current) {
        setRunning(false);
        if (refreshQueued.current) {
          refreshQueued.current = false;
          refresh();
        }
      }
    }
  };

  const change = name => event => {
    const value = event.target.value;
    setEdits(previous => ({ ...previous, [name]: value }));
  };
  const stale = !carrierDataMatches || isAvsDataStale(prefill, now);
  const carrierDataWarning = avsCarrierDataWarning(
    carrierokRefreshed,
    isDummy,
    carrierDataMatches,
    prefill,
    now
  );
  const disabledReason = avsRunDisabledReason(prefill, values);
  const mailing = (prefill && prefill.mailing_address) || {
    street: carrierok.mailing_address_street || '',
    city: carrierok.mailing_address_city || '',
    state: carrierok.mailing_address_state || '',
    zip_code: carrierok.mailing_address_zip_code || ''
  };

  return (
    <section
      className="verification-panel avs-panel"
      aria-label="Wells Fargo AVS"
    >
      <header className="verification-panel-header">
        <div>
          <h2 className="verification-panel-title">AVS — Wells Fargo</h2>
          <p className="verification-subtitle">
            Validate the carrier’s bank account and business details.
          </p>
        </div>
        {prefill && (
          <Badge result={avsFreshnessResult(carrierokRefreshed, stale)} />
        )}
      </header>
      <div className="verification-actions">
        <button
          className="verification-button verification-button-primary"
          type="button"
          onClick={run}
          disabled={Boolean(disabledReason) || loading || running || disabled}
        >
          {running
            ? 'Running AVS check…'
            : checks.length
            ? 'Recheck with form values'
            : 'Run AVS Check'}
        </button>
        <button
          className="verification-button"
          type="button"
          onClick={() => refresh(true)}
          disabled={loading || running}
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
        <span className="verification-subtitle">
          {lastRefreshed
            ? `Last refreshed ${moment(lastRefreshed).fromNow()}`
            : 'Not refreshed yet'}
        </span>
      </div>
      {(error || disabled) && (
        <p className="verification-error" role="alert">
          {disabled
            ? 'AVS is currently disabled. Contact your administrator to enable it.'
            : error}
        </p>
      )}
      {!prefill && loading && (
        <p className="verification-empty" role="status">
          Loading AVS data…
        </p>
      )}
      {prefill && (
        <>
          <h3 className="verification-section-title">
            Inputs sent to AVS · Editable
          </h3>
          {carrierDataWarning && (
            <p className="verification-warning" role="status">
              {carrierDataWarning}
            </p>
          )}
          <div className="verification-fields">
            <AvsField
              label="Company name"
              wide
              value={values.business_name}
              onChange={change('business_name')}
            />
            <AvsField
              label="Tax ID"
              wide
              value={values.tax_id}
              onChange={change('tax_id')}
            />
            <AvsField
              label="Bank account number"
              value={
                prefill.has_account
                  ? `•••• ${prefill.account_number_redacted}`
                  : 'No default ACH account'
              }
            />
            <AvsField
              label="Routing number"
              value={values.routing_number}
              onChange={change('routing_number')}
              inputMode="numeric"
              maxLength={9}
            />
          </div>
          <p className="verification-subtitle">
            Uses the default ACH account in Payment Methods. Manual account
            entry is not available here yet.
          </p>
          <h3 className="verification-section-title">Physical address</h3>
          <div className="verification-fields">
            <AvsField
              label="Street address"
              wide
              value={values.street}
              onChange={change('street')}
            />
          </div>
          <div className="verification-fields verification-fields-three">
            <AvsField
              label="City"
              value={values.city}
              onChange={change('city')}
            />
            <AvsField
              label="State"
              value={values.state}
              onChange={change('state')}
            />
            <AvsField
              label="Zip code"
              value={values.zip_code}
              onChange={change('zip_code')}
            />
          </div>
          <h3 className="verification-section-title">
            Owner and mailing details · Reference only
          </h3>
          <p className="verification-subtitle">
            Owner names and mailing addresses are not included in the current
            AVS check.
          </p>
          <div className="verification-fields">
            <AvsField
              label="Owner first name"
              value={prefill.owner_first_name}
            />
            <AvsField label="Owner last name" value={prefill.owner_last_name} />
            <AvsField
              label="Mailing street address"
              wide
              value={mailing.street}
            />
          </div>
          <div className="verification-fields verification-fields-three">
            <AvsField label="Mailing city" value={mailing.city} />
            <AvsField label="Mailing state" value={mailing.state} />
            <AvsField label="Mailing zip code" value={mailing.zip_code} />
          </div>
          <p className="verification-subtitle">
            CarrierOK last refreshed:{' '}
            {formatAvsDate(prefill.verification_last_refreshed)}
          </p>
          {disabledReason && (
            <p className="verification-empty">{disabledReason}</p>
          )}
        </>
      )}
      <AvsHistory checks={checks} />
    </section>
  );
};

export default function AvsPanel({ paymentProfile, verification }) {
  if (!paymentProfile || !paymentProfile.id) {
    return (
      <p className="verification-empty">
        Select a payment profile to view AVS.
      </p>
    );
  }
  return (
    <AvsPanelContent
      key={paymentProfile.id}
      paymentProfile={paymentProfile}
      verification={verification}
    />
  );
}
